import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutData } from '@/mocks/aboutData';

export default function AboutPage() {
  const { t, i18n } = useTranslation();
  const zh = i18n.getFixedT('zh');
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于 hlw 平台",
    description: "了解 hlw 的品牌故事、核心价值观、发展历程、荣誉资质以及联系方式。",
    url: "https://www.hlw.com/about",
    inLanguage: "zh-CN",
    about: {
      "@type": "Organization",
      name: "hlw",
      url: "https://www.hlw.com",
      foundingDate: "2020",
      description: "hlw 是一站式数字化解决方案平台，致力于让技术更简单。",
      address: {
        "@type": "PostalAddress",
        addressLocality: "北京市",
        addressRegion: "海淀区",
        streetAddress: "中关村科技园创新大厦18层",
      },
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "400-888-9999",
        contactType: "customer service",
        email: "contact@hlw.com",
      },
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: aboutData.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q9oqsnlsngrm4llu60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          '提交失败，请稍后再试';

        if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormState('error');
          setFormError('提交失败，请稍后再试');
        } else {
          setFormState('error');
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
        }
      }
    } catch {
      setFormState('error');
      setFormError('网络异常，请稍后再试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - hlw 平台"
        description="了解 hlw 的品牌故事、核心价值观、发展历程、荣誉资质。hlw 成立于2020年，服务超过10,000家企业客户，是您值得信赖的数字化合作伙伴。"
        keywords="hlw,hlw官网,关于hlw,hlw品牌,hlw联系方式,hlw荣誉"
        canonicalUrl="/about"
        schemaJson={aboutSchema}
      />
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16">
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), href: '/' },
              { label: t('nav.about') },
            ]}
          />

          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mt-4 mb-8">
            {aboutData.brandStory.title}
          </h1>

          {/* Brand Story */}
          <section className="mb-16 md:mb-20">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
              <div className="lg:w-1/2">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                  {aboutData.brandStory.subtitle}
                </h2>
                <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
                  {aboutData.brandStory.content.split('\n\n').map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </div>
              <div className="lg:w-1/2 w-full">
                <div className="rounded-lg overflow-hidden">
                  <img
                    src={aboutData.brandStory.image}
                    alt="hlw 品牌"
                    className="w-full h-auto object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="mb-16 md:mb-20">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
              {aboutData.values.title || '核心价值观'}
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {aboutData.values.map((v) => (
                <div
                  key={v.title}
                  className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                    <i className={`${v.icon} text-lg text-accent-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-1">{v.title}</h3>
                  <p className="text-sm text-foreground-600">{v.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Milestones */}
          <section className="mb-16 md:mb-20">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
              {aboutData.milestones.title || '发展历程'}
            </h2>
            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-background-200 -translate-x-1/2 hidden md:block"></div>
              <div className="flex flex-col gap-6">
                {aboutData.milestones.map((m, idx) => (
                  <div
                    key={m.year}
                    className={`flex flex-col md:flex-row items-center gap-4 ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="md:w-1/2 flex md:justify-end">
                      <div className={`p-4 md:p-5 rounded-lg bg-background-100 border border-background-200/70 w-full ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                        <span className="text-accent-500 font-bold text-lg">{m.year}</span>
                        <p className="text-sm text-foreground-700 mt-1">{m.event}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex w-4 h-4 rounded-full bg-accent-500 flex-shrink-0 z-10"></div>
                    <div className="md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-16 md:mb-20">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
              {aboutData.reputation.title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {aboutData.reputation.items.map((item) => (
                <div
                  key={item.name}
                  className="flex items-start gap-3 p-5 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 flex-shrink-0">
                    <i className={`${item.icon} text-secondary-600 text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">{item.name}</h3>
                    <span className="text-xs text-foreground-500">{item.year}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16 md:mb-20">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
              {aboutData.faqs.length > 0 ? (aboutData.contact.title === '联系我们' ? '常见问题' : aboutData.contact.title) : ''}
            </h2>
            <div className="max-w-3xl mx-auto flex flex-col gap-3">
              {aboutData.faqs.map((faq, idx) => (
                <details
                  key={idx}
                  className="group p-5 rounded-lg bg-background-100 border border-background-200/70 cursor-pointer"
                >
                  <summary className="text-sm md:text-base font-medium text-foreground-950 list-none flex items-center justify-between">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform duration-200 w-5 h-5 flex items-center justify-center"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </p>
                </details>
              ))}
            </div>
            <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
          </section>

          {/* Contact Form */}
          <section id="contact" className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                {aboutData.contact.title}
              </h2>
              <p className="text-sm text-foreground-600">{aboutData.contact.subtitle}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-8 p-5 rounded-lg bg-background-100 border border-background-200/70">
              <div className="flex-1 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100">
                  <i className="ri-mail-line text-primary-600 text-sm"></i>
                </div>
                <span className="text-sm text-foreground-700">{aboutData.contact.email}</span>
              </div>
              <div className="flex-1 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100">
                  <i className="ri-phone-line text-accent-600 text-sm"></i>
                </div>
                <span className="text-sm text-foreground-700">{aboutData.contact.phone}</span>
              </div>
              <div className="flex-1 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100">
                  <i className="ri-map-pin-line text-secondary-600 text-sm"></i>
                </div>
                <span className="text-sm text-foreground-700">{aboutData.contact.address}</span>
              </div>
            </div>

            <form
              id="about-contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/70"
            >
              {formState === 'success' && (
                <div className="mb-6 p-4 rounded-md bg-green-50 border border-green-200 text-sm text-green-700">
                  <i className="ri-checkbox-circle-line mr-2"></i>
                  {t('about.contact.success')}
                </div>
              )}
              {formState === 'error' && (
                <div className="mb-6 p-4 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                  <i className="ri-error-warning-line mr-2"></i>
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t('form.name')}
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    placeholder={t('form.namePlaceholder')}
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t('form.email')}
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    placeholder={t('form.emailPlaceholder')}
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t('form.phone')}
                  </label>
                  <input
                    type="tel"
                    id="contact-phone"
                    name="phone"
                    placeholder={t('form.phonePlaceholder')}
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t('form.company')}
                  </label>
                  <input
                    type="text"
                    id="contact-company"
                    name="company"
                    placeholder={t('form.companyPlaceholder')}
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  {t('form.message')}
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  required
                  placeholder={t('form.messagePlaceholder')}
                  className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1 text-right">限 500 字</p>
              </div>

              {/* Honeypot */}
              <div className="absolute opacity-0 pointer-events-none" style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              <button
                type="submit"
                disabled={formState === 'loading'}
                className="w-full py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {formState === 'loading' ? '发送中...' : t('about.contact.submit')}
              </button>
            </form>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}